import React, { useState } from 'react';
import {
    Button,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Box
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import i18n from 'i18';
import { StyledDialog } from '../../components/StyledDialog';
import PropTypes from 'prop-types';
import { DangerBulkIcon, TrashBulkIcon, } from '../../icons-lib/bulkIcons';
import { useTheme } from '@mui/material/styles';

const defaultDialogIcon = DangerBulkIcon;

const dialogIconsByType = {
    delete: TrashBulkIcon,
    // approve: DangerBulkIcon,
    // decline: DangerBulkIcon,
    // review: DangerBulkIcon,
    // active: DangerBulkIcon,
    // rejected: DangerBulkIcon,
};

const ConfirmDialog = ({
    dialogOpen = false,
    showInput = false,
    onCancel,
    onConfirm,
    type = 'delete',
    accentColor = 'error',
    title = i18n.t('dialog.defaultTitle'),
    subtitle = i18n.t('dialog.defaultSubtitle'),
    cancelLabel = i18n.t('buttons.cancel'),
    confirmLabel = i18n.t('buttons.confirm')
}) => {
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const theme = useTheme();

    const handleDialogCancel = () => {
        onCancel();
    };

    const handleDialogConfirm = async () => {
        setLoading(true);
        if (showInput) {
            await onConfirm(type, inputValue);
        } else {
            await onConfirm(type);
        }
        setLoading(false);
    };

    const Icon = dialogIconsByType[type] || defaultDialogIcon;

    return (
        <StyledDialog
            open={dialogOpen}
            onClose={handleDialogCancel}
            maxWidth={'sm'}
        >
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        // padding: theme.spacing(4, 4, 0),
                        gap: theme.spacing(2)
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            textAlign: 'center',
                            padding: '12px 28px',
                            border: 'transparent',
                            borderRadius: '32px',
                            background: theme.palette.gradients[accentColor]
                        }}
                    >
                        {Icon && <Icon />}
                    </Box>
                    <Typography variant="h6">{title}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {subtitle}
                    </Typography>
                </Box>
                {showInput && (
                    <TextField
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        placeholder="Enter your text"
                        multiline
                        rows={3}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    fullWidth
                    color="secondary"
                    variant="outlined"
                    disabled={loading}
                    onClick={handleDialogCancel}
                >
                    {cancelLabel}
                </Button>
                <LoadingButton
                    fullWidth
                    variant="contained"
                    loading={loading}
                    onClick={handleDialogConfirm}
                    sx={{
                        "background": theme.palette.gradients[accentColor],
                        '&:hover': {
                            background: theme.palette.gradients[accentColor]
                        }
                    }}
                >
                    {confirmLabel}
                </LoadingButton>
            </DialogActions>
        </StyledDialog>
    );
};

ConfirmDialog.propTypes = {
    dialogOpen: PropTypes.bool,
    showInput: PropTypes.bool,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    icon: PropTypes.elementType,
    accentColor: PropTypes.oneOf([
        'error',
        'warning',
        'info',
        'success',
        'primary',
        'secondary'
    ]),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    cancelLabel: PropTypes.string,
    confirmLabel: PropTypes.string
};

export default ConfirmDialog;
